import { EHR_SYNC_REFRESH_GATED_MESSAGE, isOrgEhrUsable, orgEhrUnusableMessage } from "@utils/ehrActions";
import { formatRelativeTime } from "@utils/formatRelativeTime";
import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Alert, Badge, Button, Dialog, Headline, Small } from "@fronterahealth/frontera-ui-components";
import { Switch } from "@fronterahealth/frontera-ui-components/ui/switch";

import {
  ApiLearnerManualGoalSyncStatusChoices,
  CrLearningTreeQuery,
  EhrConnectionStatus,
  useCrLearningTreeQuery,
  useLearnerManualGoalSyncByIdQuery,
  useLockCrLearningTreeMutation,
  useSaveAndLockCrLearningTreeItemsMutation,
  useSaveCrLearningTreeRolesMutation,
  useUnlockCrLearningTreeBranchMutation,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { notifyError } from "@components/notifications/notifications";
import {
  ChecksPanel,
  ConfirmCard,
  ConfirmQuestion,
  Legend,
  Preview,
  TreeRow,
} from "@pages/ClinicalReportDetails/LearningTreeReview/LearningTreeReviewParts";
import {
  CrLevel,
  LOCAL_CHECK_CODES,
  ROLE_LABEL,
  ReviewNode,
  RoleSource,
  TreeRole,
  changedRoles,
  checks,
  goalOf,
  keepPendingEdits,
  lockImportsData,
  previewGoals,
  recordsData,
  resetBranch,
  roleOptions,
  setRole,
} from "@pages/ClinicalReportDetails/LearningTreeReview/learningTreeCascade";
import { useEHRConnection } from "@providers/EHRConnectionProvider";
import { useReport } from "@providers/ReportProvider";

type Tree = NonNullable<CrLearningTreeQuery["crLearningTree"]>;
type ServerNode = Tree["nodes"][number];
type ReportChange = Tree["reportChanges"][number]["changes"][number];
type Tab = "all" | "confirm" | "user" | "open" | "notimported";

const toReviewNode = (n: ServerNode): ReviewNode => ({
  externalId: n.externalId,
  parentExternalId: n.parentExternalId ?? null,
  name: n.name ?? "",
  dataType: n.dataType ?? null,
  dataTypeLabel: n.dataTypeLabel ?? null,
  progressType: n.progressType ?? null,
  hasGoal: n.hasGoal ?? null,
  isBehavior: n.isBehavior ?? null,
  removed: n.removed,
  locked: n.locked,
  status: n.status ?? null,
  movedInCr: n.movedInCr,
  suggestedRole: (n.suggestedRole as TreeRole | null) ?? null,
  ruleRole: (n.ruleRole as TreeRole | null) ?? null,
  crLevel: (n.crLevel as CrLevel | null) ?? null,
  approvedIn: n.approvedIn ?? [],
  goalApprovedIn: n.goalApprovedIn ?? [],
  usedIn: n.usedIn ?? [],
  role: (n.role as TreeRole | null) ?? null,
  roleSource: (n.roleSource as RoleSource | null) ?? null,
  confirmKind: n.confirmKind ?? null,
  confirmAnswered: n.confirmAnswered,
  lockedGoalExternalId: n.locked ? n.goalExternalId ?? null : null,
});

/** One line of the before-lock confirmation for an open report. */
const describeReportChange = (c: ReportChange): string => {
  const name = c.name ?? c.externalId;
  switch (c.kind) {
    case "MOVE":
      return `${name} moves from ${c.fromGoalName ?? "its goal"} to ${c.toGoalName ?? "its new goal"}`;
    case "TARGET_TO_GOAL":
      return `${name} becomes a goal`;
    case "GOAL_TO_TARGET":
      return `${name} becomes a target`;
    case "ARCHIVE_GOAL":
      return `${name} is no longer a goal and is removed from the report`;
    case "ARCHIVE_TARGET":
      return `${name} is removed from the report`;
    default:
      return name;
  }
};

/** Next to the disabled Lock. True for every blocked case (not connected, org
 * login failed, sync not cleared); the specific reason is shown by Refresh Now,
 * which is always on screen. */
const LOCK_BLOCKED_MESSAGE = "Lock waits for the EHR connection. Your choices are saved.";

/** Lock waits for the EHR connection only when it imports something. A first
 * Lock or "Lock changes" always does; Save and lock of only folders, Not
 * imported items or items under a locked Skip branch fetches nothing (FDM
 * skips the check the same way). */
const isLockBlocked = (
  refreshBlocked: React.ReactNode,
  reviewingNewItems: boolean,
  nodes: ReviewNode[],
  lockIds: Set<string>,
): boolean => {
  if (!refreshBlocked) return false;
  if (!reviewingNewItems) return true;
  return lockImportsData(nodes, lockIds);
};

const UNLOCK_WARNING =
  "Changing roles may change goals and targets in open reports. Approved items can't change. Signed reports never change.";

const listNames = (items: ReviewNode[]): string => items.map((i) => i.name).join(", ");

/** The two answers a Needs confirm card offers: keep the suggestion, or the other meaning. */
const confirmQuestion = (nodes: ReviewNode[], node: ReviewNode): ConfirmQuestion | null => {
  const kids = nodes.filter((n) => n.parentExternalId === node.externalId && !n.removed);
  const dataKids = kids.filter(recordsData);
  switch (node.confirmKind) {
    case "MIXED":
      return {
        question: `How should we import “${node.name}”?`,
        detail: "Some items under it are goals and some record data themselves.",
        keep: {
          title: "Each item is its own goal, like its siblings",
          preview: kids.map((k) => `◆ ${k.name}`).join("   "),
        },
        other: {
          title: `“${node.name}” is a goal, the items that record data are its targets`,
          preview: `◆ ${node.name} → • ${listNames(dataKids)}`,
          role: "GOAL",
        },
      };
    case "DATA_UNDER_DATA": {
      const goal = goalOf(nodes, node.externalId);
      const goalName = nodes.find((n) => n.externalId === goal)?.name ?? "its goal";
      return {
        question: `“${node.name}” has its own data and data items under it`,
        detail: "This usually means a cumulative target with individual targets under it.",
        keep: {
          title: `All of them are targets of “${goalName}”`,
          preview: `◆ ${goalName} → • ${node.name}, ${listNames(dataKids)}`,
        },
        other: {
          title: `“${node.name}” is a goal, the items under it are its targets`,
          preview: `◆ ${node.name} (data on goal) → • ${listNames(dataKids)}`,
          role: "GOAL",
        },
      };
    }
    default:
      return null;
  }
};

const needsAnswer = (n: ReviewNode): boolean => !!n.confirmKind && !n.confirmAnswered && !n.locked;
const isSkipped = (n: ReviewNode): boolean => n.role === "SKIP_ITEM" || n.role === "SKIP_BRANCH";

const matchesTab = (tab: Tab, n: ReviewNode): boolean => {
  switch (tab) {
    case "confirm":
      return needsAnswer(n);
    case "user":
      return n.roleSource === "PERSON" && !n.locked;
    case "open":
      return !n.locked && !n.removed;
    case "notimported":
      return isSkipped(n);
    default:
      return true;
  }
};

/**
 * Rows the tree shows: the tab's matches and the items above each, for
 * context. Hide skipped items leaves skipped rows out (their non-skipped items
 * still bring them in as parents); a row that still needs an answer stays.
 */
const visibleIds = (nodes: ReviewNode[], tab: Tab, hideSkipped: boolean): Set<string> => {
  const parentOf = new Map(nodes.map((n) => [n.externalId, n.parentExternalId]));
  const hiding = hideSkipped && tab !== "notimported";
  const shown = new Set<string>();
  for (const n of nodes) {
    if (!matchesTab(tab, n) || (hiding && isSkipped(n) && !needsAnswer(n))) continue;
    let id: string | null | undefined = n.externalId;
    while (id && !shown.has(id)) {
      shown.add(id);
      id = parentOf.get(id);
    }
  }
  return shown;
};

/** Hover state after a row is entered or left (leaving another row keeps it). */
const hoveredAfter = (prev: string | null, id: string, hovering: boolean): string | null => {
  if (hovering) return id;
  return prev === id ? null : prev;
};

const childrenOf = (nodes: ReviewNode[]): Map<string | null, ReviewNode[]> => {
  const ids = new Set(nodes.map((n) => n.externalId));
  const map = new Map<string | null, ReviewNode[]>();
  for (const n of nodes) {
    const parent = n.parentExternalId && ids.has(n.parentExternalId) ? n.parentExternalId : null;
    map.set(parent, [...(map.get(parent) ?? []), n]);
  }
  return map;
};

/** A hovered row and everything under it, highlighted on the right. */
const subtreeOf = (rootId: string | null, children: Map<string | null, ReviewNode[]>): Set<string> => {
  const ids = new Set<string>();
  const stack = rootId ? [rootId] : [];
  while (stack.length) {
    const id = stack.pop() as string;
    if (ids.has(id)) continue;
    ids.add(id);
    for (const kid of children.get(id) ?? []) stack.push(kid.externalId);
  }
  return ids;
};

interface Mode {
  reviewingNewItems: boolean;
  firstReviewWithImports: boolean;
  editingLocked: boolean;
}

const headerCopy = (mode: Mode): { title: string; subtitle: string } => {
  if (mode.reviewingNewItems) {
    return {
      title: "Review new items",
      subtitle: "These items are new in CentralReach. Lock them to import their data; nothing is fetched until you do.",
    };
  }
  if (mode.firstReviewWithImports) {
    return {
      title: "Importing from CentralReach",
      subtitle: "Items already imported are locked. Check the rest, then lock; nothing new is fetched until you do.",
    };
  }
  if (mode.editingLocked) {
    return {
      title: "Learning tree",
      subtitle: "Locked items show how this tree was imported. Unlock a branch to change it.",
    };
  }
  return {
    title: "Importing from CentralReach",
    subtitle: "We read the tree structure first, you check it, and only then do we import data.",
  };
};

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? "" : "s"}`;

const lockLabelOf = (mode: Mode, errorCount: number, nothingToLock: boolean, needsNothing: boolean): string => {
  if (errorCount) return `Fix ${plural(errorCount, "item")} first`;
  if (mode.reviewingNewItems) return "Save and lock";
  if (nothingToLock) return "Unlock an item to change it";
  if (mode.editingLocked) return "Lock changes";
  return needsNothing ? "Looks right, lock and import" : "Lock structure and import data";
};

const ReviewAlert: React.FC<{ mode: Mode; itemCount: number; confirmCount: number }> = ({
  mode,
  itemCount,
  confirmCount,
}) => {
  if (mode.reviewingNewItems) {
    return (
      <Alert
        appearance="warn"
        title="New items in CentralReach need your answer"
        text="Everything you reviewed before is locked and won't change. Set up the new items below; we'll fetch data only for them."
      />
    );
  }
  if (mode.firstReviewWithImports) {
    return (
      <Alert
        appearance="neutral"
        title="Check how we'll import the rest of this tree"
        text={`Goals and targets already imported are locked as they are. Check the other items and lock. Unlock a branch to change an imported item. ${UNLOCK_WARNING}`}
      />
    );
  }
  if (mode.editingLocked) {
    return (
      <Alert
        appearance="neutral"
        title="The structure you reviewed is locked"
        text={`Goals, targets and their graphs stay the same across syncs and reports. Unlock a branch to change it. ${UNLOCK_WARNING}`}
      />
    );
  }
  if (confirmCount) {
    const verb = confirmCount === 1 ? "needs" : "need";
    return (
      <Alert
        appearance="warn"
        title="Check how we'll import this tree before we import data"
        text={`We read ${itemCount} items and suggested a role for each. ${confirmCount} ${verb} your answer because the same CentralReach shape can mean different things. Once you import, this structure is locked.`}
      />
    );
  }
  return (
    <Alert
      appearance="neutral"
      title="Check how we'll import this tree"
      text={`We read ${itemCount} items and suggested a role for each. Nothing looks unclear, but no data is imported until you check it. Once you import, this structure is locked.`}
    />
  );
};

const FooterText: React.FC<{
  mode: Mode;
  goalCount: number;
  targetCount: number;
  skipped: number;
  waitingCount: number;
  lockCount: number;
  editable: number;
}> = ({ mode, goalCount, targetCount, skipped, waitingCount, lockCount, editable }) => {
  if (mode.reviewingNewItems) {
    const unlocked = lockCount - waitingCount;
    const extra = unlocked ? ` and ${plural(unlocked, "unlocked item")}` : "";
    return <>{`${plural(waitingCount, "new item")}${extra} to lock`}</>;
  }
  if (mode.editingLocked) {
    return (
      <>{editable ? `${plural(editable, "unlocked item")} · lock to apply your changes` : "Everything is locked"}</>
    );
  }
  return (
    <>
      We&apos;ll fetch data for <b>{goalCount + targetCount}</b> items ({goalCount} goals, {targetCount} targets).{" "}
      {skipped} skipped items cost no requests.
    </>
  );
};

/** Why Refresh Now is off: the same EHR gate as the other EHR buttons (Import Now, Refresh from EHR).
 * Lock uses it too: a lock starts the import, and FDM keeps the lock when the
 * import can't start, so without this gate a lock leaves the tree locked with
 * no data and Refresh Now off. */
const useRefreshBlocked = (): React.ReactNode => {
  const { isEhrSyncCleared, isOrgUsernamePassword, ehrConnectionStatus, hasCredentials } = useEHRConnection();
  if (!isEhrSyncCleared) return <EhrSyncGatedNotice message={EHR_SYNC_REFRESH_GATED_MESSAGE} />;
  if (isOrgUsernamePassword && !isOrgEhrUsable(ehrConnectionStatus)) {
    return <Small colorType="secondary">{orgEhrUnusableMessage(ehrConnectionStatus)}</Small>;
  }
  if (!isOrgUsernamePassword && ehrConnectionStatus !== EhrConnectionStatus.Connected && !hasCredentials) {
    return <Small colorType="secondary">Connect your EHR to refresh the learning tree.</Small>;
  }
  return null;
};

const ReportChangesDialog: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
  reportChanges: Tree["reportChanges"];
  onConfirm: () => void;
}> = ({ open, setOpen, reportChanges, onConfirm }) => (
  <Dialog
    open={open}
    setOpen={setOpen}
    title="This lock changes open reports"
    size="xl"
    primaryButton={{ text: "Confirm and lock", onClick: onConfirm }}
    secondaryButton={{ text: "Cancel", onClick: () => setOpen(false) }}
  >
    <div className="space-y-3">
      {reportChanges.map((impact) => (
        <div key={impact.reportId} className="rounded-lg border border-[#f9b72a] bg-[#fffbeb] px-4 py-3">
          <div className="text-sm font-semibold text-[#332200]">{impact.label} (open report) will change:</div>
          <ul className="mt-1 list-disc pl-5">
            {impact.changes.map((c) => (
              <li key={`${c.externalId}-${c.kind}`}>
                <Small>{describeReportChange(c)}</Small>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <Small colorType="secondary">
        Summaries and charts for moved or new items will be written again. Signed reports never change.
      </Small>
    </div>
  </Dialog>
);

const UndoToast: React.FC<{ message: string; onUndo: () => void; onDismiss: () => void }> = ({
  message,
  onUndo,
  onDismiss,
}) => (
  <div className="fixed bottom-6 left-1/2 z-[90] flex max-w-[760px] -translate-x-1/2 items-center gap-3.5 rounded-lg bg-[#0e2528] px-4 py-2.5 text-sm text-white">
    <span>{message}</span>
    <button type="button" className="text-[#b1e4e3] underline" onClick={onUndo}>
      Undo
    </button>
    <button type="button" aria-label="Dismiss" className="text-[#b1e4e3]" onClick={onDismiss}>
      ✕
    </button>
  </div>
);

interface TreeRowsProps {
  parent: string | null;
  depth: number;
  nodes: ReviewNode[];
  childMap: Map<string | null, ReviewNode[]>;
  shown: Set<string>;
  isOpen: (id: string) => boolean;
  highlighted: string | null;
  menuFor: string | null;
  choices: Record<string, "keep" | "other">;
  onToggle: (id: string) => void;
  onMenu: (id: string | null) => void;
  onRoleChange: (node: ReviewNode, value: TreeRole | "RESET") => void;
  onKeepSuggestion: (node: ReviewNode) => void;
  onChoice: (id: string, choice: "keep" | "other") => void;
  onHover: (id: string, hovering: boolean) => void;
  onAskUnlock: (node: ReviewNode) => void;
}

/** One row with its Needs confirm card, then the rows under it when open. */
const TreeRowWithCard: React.FC<TreeRowsProps & { node: ReviewNode }> = (props) => {
  const { node, depth, nodes, childMap, choices } = props;
  const kids = childMap.get(node.externalId) ?? [];
  const open = props.isOpen(node.externalId);
  const question = needsAnswer(node) ? confirmQuestion(nodes, node) : null;
  const choice = choices[node.externalId] ?? "keep";
  const menuOpen = props.menuFor === node.externalId;
  return (
    <>
      <TreeRow
        nodes={nodes}
        node={node}
        depth={depth}
        hasChildren={kids.length > 0}
        open={open}
        highlighted={props.highlighted === node.externalId}
        menuOpen={menuOpen}
        options={menuOpen ? roleOptions(nodes, node.externalId) : []}
        onToggle={() => props.onToggle(node.externalId)}
        onMenu={(isOpen) => props.onMenu(isOpen ? node.externalId : null)}
        onPick={(value) => props.onRoleChange(node, value)}
        onHover={(hovering) => props.onHover(node.externalId, hovering)}
        onUnlock={() => props.onAskUnlock(node)}
      />
      {question && (
        <div style={{ paddingLeft: depth * 22 }}>
          <ConfirmCard
            question={question}
            choice={choice}
            onChoice={(c) => props.onChoice(node.externalId, c)}
            onConfirm={() =>
              choice === "keep" ? props.onKeepSuggestion(node) : props.onRoleChange(node, question.other.role)
            }
          />
        </div>
      )}
      {open && <TreeRows {...props} parent={node.externalId} depth={depth + 1} />}
    </>
  );
};

const TreeRows: React.FC<TreeRowsProps> = (props) => (
  <>
    {(props.childMap.get(props.parent) ?? [])
      .filter((n) => props.shown.has(n.externalId))
      .map((node) => (
        <TreeRowWithCard key={node.externalId} {...props} node={node} />
      ))}
  </>
);

/** Role changes: applied at once, saved as drafts, with Undo. */
const useRoleEditing = (args: {
  learnerId: string;
  nodes: ReviewNode[];
  setNodes: (nodes: ReviewNode[]) => void;
  savedRef: React.RefObject<ReviewNode[]>;
  reviewingNewItems: boolean;
  onApplied: () => void;
}) => {
  const { learnerId, nodes, setNodes, savedRef, reviewingNewItems, onApplied } = args;
  const saveRoles = useSaveCrLearningTreeRolesMutation();
  const [undo, setUndo] = useState<{ nodes: ReviewNode[]; message: string } | null>(null);

  // Drafts survive a reload: every change is saved as it's made.
  const persist = (next: ReviewNode[]) => {
    const changes = changedRoles(savedRef.current, next);
    if (!changes.length || reviewingNewItems) return;
    // Saved only once FDM has it: a failed save stays pending, so the next
    // edit, a reload or Lock sends it again.
    saveRoles.mutate(
      { learnerId, changes },
      {
        onSuccess: () => {
          savedRef.current = next;
        },
        onError: () => notifyError("Couldn't save your change. Try again."),
      },
    );
  };

  const apply = (next: ReviewNode[], message: string) => {
    onApplied();
    setUndo({ nodes, message });
    setNodes(next);
    persist(next);
  };

  const onRoleChange = (node: ReviewNode, value: TreeRole | "RESET") => {
    if (value === "RESET") {
      apply(resetBranch(nodes, node.externalId), `Reset “${node.name}” and the items under it to our suggestion.`);
      return;
    }
    const { nodes: next, counts } = setRole(nodes, node.externalId, value);
    const moved = (Object.entries(counts) as [TreeRole, number][])
      .filter(([, n]) => n)
      .map(([r, n]) => `${n} → ${ROLE_LABEL[r]}`);
    const under = moved.length ? ` Under it: ${moved.join(", ")}.` : "";
    apply(next, `“${node.name}” is now ${ROLE_LABEL[value]}.${under}`);
  };

  const onKeepSuggestion = (node: ReviewNode) => {
    const next = nodes.map((n) =>
      n.externalId === node.externalId ? { ...n, confirmAnswered: true, roleSource: "PERSON" as RoleSource } : n,
    );
    apply(next, `Kept our suggestion for “${node.name}”.`);
  };

  const onUndo = () => {
    if (!undo) return;
    setNodes(undo.nodes);
    persist(undo.nodes);
    setUndo(null);
  };

  return { saveRoles, undo, setUndo, onUndo, onRoleChange, onKeepSuggestion };
};

/**
 * Lock, and Save and lock for new items. A Lock or a refresh runs on the
 * refreshing-data step, which shows progress and errors as for any import
 * (Retry included): Lock hands it the stamp from before its import so it waits
 * for that one instead of starting another. What a lock changes in open
 * reports is shown first; the user confirms.
 */
const useLockFlow = (args: {
  learnerId: string;
  reportId: string;
  nodes: ReviewNode[];
  savedRef: React.RefObject<ReviewNode[]>;
  reviewingNewItems: boolean;
  lockIds: Set<string>;
  waitingIds: Set<string>;
  syncLastUpdated: string | null | undefined;
  saveRoles: ReturnType<typeof useSaveCrLearningTreeRolesMutation>;
  refetch: () => Promise<{ data?: CrLearningTreeQuery }>;
  watchImport: (since: string | null | undefined) => void;
  backToGoals: () => void;
}) => {
  const { learnerId, reportId, nodes, savedRef, reviewingNewItems, lockIds, waitingIds } = args;
  const { syncLastUpdated, saveRoles, refetch, watchImport, backToGoals } = args;
  const lockTree = useLockCrLearningTreeMutation();
  const saveAndLock = useSaveAndLockCrLearningTreeItemsMutation();
  const [confirming, setConfirming] = useState(false);

  const lockNow = () => {
    setConfirming(false);
    const since = syncLastUpdated;
    lockTree.mutate(
      { learnerId, reportId },
      {
        onSuccess: (res) => {
          if (!res.lockCrLearningTree?.status) {
            notifyError(res.lockCrLearningTree?.message || "Couldn't lock the structure.");
            void refetch();
            return;
          }
          watchImport(since);
        },
        onError: () => notifyError("Couldn't lock the structure."),
      },
    );
  };

  const saveAndLockNow = () => {
    setConfirming(false);
    const toLock = nodes.filter((n) => lockIds.has(n.externalId) && n.role);
    const changes = toLock.map((n) => ({
      externalId: n.externalId,
      role: n.role as TreeRole,
      source: (n.roleSource === "FOLLOWS" ? "FOLLOWS" : "PERSON") as RoleSource,
      confirmAnswered: n.confirmAnswered,
    }));
    // FDM's rule: folders, Not imported items and anything under a locked
    // Skip branch fetch nothing, so there's no import to wait for.
    const importsSomething = lockImportsData(nodes, new Set(toLock.map((n) => n.externalId)));
    const since = syncLastUpdated;
    saveAndLock.mutate(
      { learnerId, changes, reportId },
      {
        onSuccess: (res) => {
          const result = res.saveAndLockCrLearningTreeItems;
          if (!result?.status) {
            notifyError(result?.message || "Couldn't save and lock.");
            void refetch();
            return;
          }
          // Only skipped items: nothing to fetch.
          if (importsSomething) watchImport(since);
          else backToGoals();
        },
        onError: () => notifyError("Couldn't save and lock."),
      },
    );
  };

  // Save what isn't saved yet, then show the report changes or lock.
  const saveThen = (changes: ReturnType<typeof changedRoles>, lock: () => void) => {
    const confirmOrLock = async () => {
      const fresh = await refetch();
      if (fresh.data?.crLearningTree?.reportChanges?.length) setConfirming(true);
      else lock();
    };
    if (!changes.length) {
      void confirmOrLock();
      return;
    }
    saveRoles.mutate(
      { learnerId, changes },
      { onSuccess: () => void confirmOrLock(), onError: () => notifyError("Couldn't save your changes.") },
    );
  };

  const onLock = () => {
    if (!reviewingNewItems) {
      saveThen(changedRoles(savedRef.current, nodes), lockNow);
      return;
    }
    // An unlocked branch can change open reports: save it, then show what
    // changes there first, as for any lock.
    const unlockedBranch = (id: string) => lockIds.has(id) && !waitingIds.has(id);
    if (!nodes.some((n) => unlockedBranch(n.externalId))) {
      saveAndLockNow();
      return;
    }
    saveThen(
      changedRoles(savedRef.current, nodes).filter((c) => unlockedBranch(c.externalId)),
      saveAndLockNow,
    );
  };

  return {
    onLock,
    confirming,
    setConfirming,
    confirmLock: reviewingNewItems ? saveAndLockNow : lockNow,
    isLocking: lockTree.isPending || saveAndLock.isPending,
  };
};

const PageMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-full justify-center bg-limestone-50">
    <div className="mx-4 my-8 w-full max-w-[856px] rounded-lg bg-white p-8 shadow-xs">{children}</div>
  </div>
);

export const LearningTreeReview: React.FC = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { report } = useReport();
  const learnerId = report?.learner?.id || "";
  const reportId = report?.id || "";

  // Only this report's approvals block a change (FDM scopes approvedIn to it).
  const { data, isLoading, refetch } = useCrLearningTreeQuery(
    { learnerId, reportId: reportId || undefined },
    { enabled: !!learnerId, refetchOnMount: "always", refetchOnWindowFocus: false },
  );
  const tree = data?.crLearningTree ?? null;
  const reviewingNewItems = tree?.status === ApiLearnerManualGoalSyncStatusChoices.HasWaitingItems;

  const serverNodes = useMemo(() => (tree?.nodes ?? []).map(toReviewNode), [tree]);
  const [nodes, setNodes] = useState<ReviewNode[]>(serverNodes);
  const savedRef = useRef<ReviewNode[]>(serverNodes);
  const [pendingUnlock, setPendingUnlock] = useState<ReviewNode | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [choices, setChoices] = useState<Record<string, "keep" | "other">>({});
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  // View only: hides skipped rows; a skipped item holding goals stays as context.
  const [hideSkipped, setHideSkipped] = useState(false);
  const hasLockedOnServer = serverNodes.some((n) => n.locked);
  const [tab, setTab] = useState<Tab>("all");
  // Fresh server data replaces what's shown, keeping edits not saved yet
  // (adjusting state during render, not in an effect).
  const [loadedFrom, setLoadedFrom] = useState(serverNodes);
  if (loadedFrom !== serverNodes) {
    setLoadedFrom(serverNodes);
    setNodes(keepPendingEdits(savedRef.current, nodes, serverNodes));
    savedRef.current = serverNodes;
  }

  const waitingIds = useMemo(
    () => new Set(nodes.filter((n) => n.status === "WAITING" && !n.removed).map((n) => n.externalId)),
    [nodes],
  );
  // Reviewing new items locks the waiting items plus any branch unlocked meanwhile.
  const lockIds = useMemo(
    () => new Set(nodes.filter((n) => !n.removed && !n.locked).map((n) => n.externalId)),
    [nodes],
  );
  const lines = useMemo(() => {
    const local = checks(nodes, reviewingNewItems ? lockIds : undefined);
    // FDM's lines for checks only it can run (approvals in open reports, and
    // any it adds later) come with the tree. Checks the portal runs itself stay
    // local, so a row fixed here isn't held back by the last load.
    const serverOnly = (tree?.checks ?? [])
      .filter((c) => !LOCAL_CHECK_CODES.has(c.code))
      .map((c) => ({
        externalId: c.externalId,
        // Only an error blocks the lock; any other level shows as a warning.
        level: c.level === "error" ? ("error" as const) : ("warning" as const),
        code: c.code,
        message: c.message,
      }));
    return [...local, ...serverOnly];
  }, [nodes, reviewingNewItems, lockIds, tree]);
  const errors = lines.filter((l) => l.level === "error");
  const preview = useMemo(() => previewGoals(nodes), [nodes]);
  const children = useMemo(() => childrenOf(nodes), [nodes]);

  const unlockBranch = useUnlockCrLearningTreeBranchMutation();
  const onUnlock = (node: ReviewNode) => {
    setPendingUnlock(null);
    unlockBranch.mutate(
      { learnerId, externalId: node.externalId },
      {
        onSuccess: () => void refetch(),
        onError: () => notifyError("Couldn't unlock. Try again."),
      },
    );
  };

  // The client's sync stamp, shown as "Last Updated".
  const { data: syncData } = useLearnerManualGoalSyncByIdQuery(
    { learnerId },
    { enabled: !!learnerId, gcTime: 0, staleTime: 0 },
  );
  const syncLastUpdated = syncData?.learnerById?.manualGoalSyncLastUpdated;
  const watchImport = (since: string | null | undefined) =>
    void navigate("../refreshing-data", {
      state: { ...locationState, watchImportSince: since ?? null },
      replace: true,
    });
  // Only the tree: no goal or target data is fetched from here.
  const onRefreshTree = () =>
    void navigate("../refreshing-data", {
      state: { ...locationState, returnToLearningTree: true, learningTreeStructureOnly: true },
    });
  const backToGoals = () => void navigate("../select-programming", { state: locationState, replace: true });
  const refreshBlocked = useRefreshBlocked();

  const editing = useRoleEditing({
    learnerId,
    nodes,
    setNodes,
    savedRef,
    reviewingNewItems,
    onApplied: () => {
      lock.setConfirming(false);
      setMenuFor(null);
    },
  });
  const lock = useLockFlow({
    learnerId,
    reportId,
    nodes,
    savedRef,
    reviewingNewItems,
    lockIds,
    waitingIds,
    syncLastUpdated,
    saveRoles: editing.saveRoles,
    refetch,
    watchImport,
    backToGoals,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-full justify-center bg-limestone-50">
        <div className="mx-4 my-8 flex w-full max-w-[1240px] flex-col items-center justify-center gap-2 rounded-lg bg-white p-12 shadow-xs">
          <Loader className="min-h-[200px] text-text-secondary" />
        </div>
      </div>
    );
  }

  if (!tree) {
    return (
      <PageMessage>
        <Small colorType="secondary">No learning tree has been read for this client yet.</Small>
      </PageMessage>
    );
  }

  const live = nodes.filter((n) => !n.removed);
  const confirmCount = live.filter(needsAnswer).length;
  const userCount = live.filter((n) => n.roleSource === "PERSON" && !n.locked).length;
  const openCount = live.filter((n) => !n.locked).length;
  const skipped = live.filter(isSkipped).length;
  const goalCount = preview.length;
  const targetCount = preview.reduce((sum, p) => sum + p.targets.length, 0);
  const retired = nodes.filter((n) => n.removed && n.locked && (n.role === "GOAL" || n.role === "TARGET"));
  const editingLocked = hasLockedOnServer && !reviewingNewItems;
  const mode: Mode = {
    reviewingNewItems,
    editingLocked,
    // First review of a client with imports: what's imported is locked, the
    // rest is still to check.
    firstReviewWithImports: editingLocked && tree.status === ApiLearnerManualGoalSyncStatusChoices.AwaitingReview,
  };
  // A first review sends goal selection straight back here, unless the report
  // already has goals and opened the review from goal selection's banner.
  const canGoBack =
    (editingLocked || reviewingNewItems) && (!mode.firstReviewWithImports || !!locationState?.fromSelectProgramming);
  const editable = live.filter((n) => !n.locked && n.status !== "WAITING").length;
  const nothingToLock = editingLocked && editable === 0;
  const needsNothing = !confirmCount && !changedRoles(serverNodes, nodes).length;
  const { title, subtitle } = headerCopy(mode);
  const lockLabel = lockLabelOf(mode, errors.length, nothingToLock, needsNothing);
  const refreshStatusText = syncLastUpdated ? `Last Updated: ${formatRelativeTime(new Date(syncLastUpdated))}` : "";

  const tabs: [Tab, string, number][] = [
    ["all", "All items", live.length],
    ["confirm", "Needs confirm", confirmCount],
    ["user", "Changed by you", userCount],
    ...(hasLockedOnServer ? [["open", "Not locked", openCount] as [Tab, string, number]] : []),
    ["notimported", "Skipped", skipped],
  ];
  const shown = visibleIds(nodes, tab, hideSkipped);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const collapseAllIds = () => new Set(nodes.filter((n) => children.has(n.externalId)).map((n) => n.externalId));

  const nameOf = (id: string) => nodes.find((n) => n.externalId === id)?.name ?? id;
  const onShow = (id: string) => {
    setTab("all");
    setCollapsed(new Set());
    setHighlighted(id);
    document.getElementById(`tree-node-${id}`)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };

  const lockBlocked = isLockBlocked(refreshBlocked, reviewingNewItems, nodes, lockIds);
  return (
    <div className="flex min-h-full justify-center bg-limestone-50">
      <div className="mx-4 my-8 flex w-full max-w-[1240px] flex-col rounded-lg bg-white shadow-xs">
        <div className="px-8 pt-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <Headline size="medium">{title}</Headline>
            <div className="flex max-w-[420px] flex-col items-end gap-1 text-right">
              {/* Same control as Import Now on the report page. */}
              <div className="flex items-center gap-2">
                <Small>{refreshStatusText}</Small>
                <Button
                  className="ml-2"
                  onClick={onRefreshTree}
                  text="Refresh Now"
                  appearance="text"
                  style={{ height: "auto", padding: 0 }}
                  disabled={!!refreshBlocked}
                />
              </div>
              {/* Inline next to the disabled control, never tooltip-only. */}
              {refreshBlocked}
            </div>
          </div>
          <Small colorType="secondary" className="mt-2 block">
            {subtitle}
          </Small>
        </div>

        <div className="mt-6 px-8">
          <ReviewAlert mode={mode} itemCount={live.length} confirmCount={confirmCount} />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 px-8">
          <div className="flex min-w-0 flex-1 gap-5 overflow-x-auto border-b border-limestone-200">
            {tabs.map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 py-2.5 text-sm hover:text-[#24727f] ${
                  tab === key ? "border-[#24727f] text-text-primary" : "border-transparent text-text-secondary"
                }`}
              >
                {label}
                <Badge text={String(count)} appearance={key === "confirm" && count ? "warn" : "neutral"} />
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge text={`${live.length} items read`} appearance="neutral" />
            <Badge text={`${goalCount} goals · ${targetCount} targets`} appearance="success" />
            {confirmCount > 0 && <Badge text={`${confirmCount} to confirm`} appearance="warn" dot />}
          </div>
        </div>

        <div className="px-8 pb-4">
          <div className="mt-4 grid grid-cols-1 rounded-lg border border-limestone-200 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
            <div className="min-w-0">
              <div className="flex items-center justify-between gap-2 rounded-tl-lg border-b border-limestone-200 bg-white px-4 py-3">
                <span className="text-sm font-semibold text-text-primary">In CentralReach</span>
                <span className="flex items-center gap-1">
                  {/* Same labelled switch as "My Reports" on the reports table. */}
                  <span className="mr-2 flex items-center gap-2">
                    <Small colorType="primary">Hide skipped items</Small>
                    <Switch
                      size="sm"
                      aria-label="Hide skipped items"
                      checked={hideSkipped}
                      onCheckedChange={setHideSkipped}
                    />
                  </span>
                  <span className="mr-2 h-4 w-px bg-limestone-300" aria-hidden />
                  {/* One link: only the action that changes anything is offered. */}
                  <Button
                    text={collapsed.size ? "Expand all" : "Collapse all"}
                    appearance="text"
                    style={{ height: "auto", padding: 0 }}
                    onClick={() => setCollapsed(collapsed.size ? new Set() : collapseAllIds())}
                  />
                </span>
              </div>
              <div className="pb-3 pt-1">
                {shown.size ? (
                  <TreeRows
                    parent={null}
                    depth={0}
                    nodes={nodes}
                    childMap={children}
                    shown={shown}
                    isOpen={(id) => tab !== "all" || !collapsed.has(id)}
                    highlighted={highlighted}
                    menuFor={menuFor}
                    choices={choices}
                    onToggle={toggle}
                    onMenu={setMenuFor}
                    onRoleChange={editing.onRoleChange}
                    onKeepSuggestion={editing.onKeepSuggestion}
                    onChoice={(id, c) => setChoices((prev) => ({ ...prev, [id]: c }))}
                    onHover={(id, hovering) => setHovered((prev) => hoveredAfter(prev, id, hovering))}
                    onAskUnlock={(node) => {
                      setMenuFor(null);
                      setPendingUnlock(node);
                    }}
                  />
                ) : (
                  <div className="px-4 py-6 text-[13px] text-text-tertiary">No items match this filter.</div>
                )}
              </div>
            </div>
            <div className="min-w-0 border-t border-limestone-200 bg-limestone-50 lg:border-l lg:border-t-0">
              <div className="flex items-center justify-between gap-2 rounded-tr-lg border-b border-limestone-200 px-4 py-3">
                <span className="text-sm font-semibold text-text-primary">
                  {editingLocked ? "Goals and targets" : "What we'll import"}
                </span>
                <span className="text-xs text-text-tertiary">
                  {editingLocked
                    ? `${goalCount} goals · ${targetCount} targets`
                    : "structure only · no data fetched yet"}
                </span>
              </div>
              <Preview
                goals={preview}
                retired={retired}
                skipped={skipped}
                importedNotInTree={tree.importedNotInTree ?? []}
                highlighted={subtreeOf(hovered, children)}
              />
            </div>
          </div>

          <ChecksPanel
            lines={lines}
            nameOf={nameOf}
            onShow={onShow}
            onMakeFolder={(id) => {
              const node = nodes.find((n) => n.externalId === id);
              if (node) editing.onRoleChange(node, "SKIP_ITEM");
            }}
          />
        </div>

        <div className="sticky bottom-0 z-[5] rounded-b-lg border-t border-limestone-100 bg-white px-8 py-4">
          <Legend />
          <div className="mt-3 flex items-center justify-end gap-4 border-t border-limestone-100 pt-3">
            <Small colorType="secondary" className="mr-auto">
              <FooterText
                mode={mode}
                goalCount={goalCount}
                targetCount={targetCount}
                skipped={skipped}
                waitingCount={waitingIds.size}
                lockCount={lockIds.size}
                editable={editable}
              />
            </Small>
            {lockBlocked && !nothingToLock && <Small colorType="secondary">{LOCK_BLOCKED_MESSAGE}</Small>}
            {canGoBack && <Button text="Back" variant="secondary" buttonStyle="border" onClick={backToGoals} />}
            <Button
              text={lockLabel}
              variant="primary"
              onClick={() => errors.length === 0 && !lockBlocked && lock.onLock()}
              disabled={errors.length > 0 || nothingToLock || lock.isLocking || lockBlocked}
            />
          </div>
        </div>
      </div>

      {editing.undo && (
        <UndoToast message={editing.undo.message} onUndo={editing.onUndo} onDismiss={() => editing.setUndo(null)} />
      )}

      <Dialog
        open={!!pendingUnlock}
        setOpen={(open) => !open && setPendingUnlock(null)}
        title={pendingUnlock ? `Unlock “${pendingUnlock.name}” and the items under it?` : ""}
        size="lg"
        primaryButton={{ text: "Unlock", onClick: () => pendingUnlock && onUnlock(pendingUnlock) }}
        secondaryButton={{ text: "Cancel", onClick: () => setPendingUnlock(null) }}
      >
        <Small colorType="secondary">{UNLOCK_WARNING} Nothing changes until you lock again.</Small>
      </Dialog>

      <ReportChangesDialog
        open={lock.confirming}
        setOpen={lock.setConfirming}
        reportChanges={tree.reportChanges ?? []}
        onConfirm={lock.confirmLock}
      />
    </div>
  );
};
